import React from 'react';
import { Heart } from 'lucide-react';
import { useFavorites } from '../favorites';

/**
 * Small heart toggle for saving activities/experiences to favourites (localStorage).
 * Renders as an absolute-positioned overlay on top-right of a card by default.
 *
 * Props:
 *  - item: { id, name, image, city_slug, category_key, subcategory, rating }
 *  - variant: 'overlay' (default: absolute top-right white glass) | 'inline' (compact button)
 *  - stopPropagation: default true — prevents triggering parent Link click
 */
export default function FavoriteButton({ item, variant = 'overlay', stopPropagation = true, className = '' }) {
  const { isFavorite, toggle } = useFavorites();
  if (!item || !item.id) return null;
  const active = isFavorite(item.id);

  const handleClick = (e) => {
    if (stopPropagation) { e.preventDefault(); e.stopPropagation(); }
    toggle(item);
  };

  if (variant === 'inline') {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-label={active ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
        data-testid={`fav-toggle-${item.id}`}
        aria-pressed={active}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition ${
          active
            ? 'bg-red-500/20 text-red-300 border border-red-400/40'
            : 'border border-white/20 text-white/85 hover:bg-white/10'
        } ${className}`}
      >
        <Heart size={13} fill={active ? 'currentColor' : 'none'} strokeWidth={2} />
        {active ? 'Salvato' : 'Salva'}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={active ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
      aria-pressed={active}
      data-testid={`fav-toggle-${item.id}`}
      className={`absolute top-2 right-2 z-20 w-9 h-9 rounded-full flex items-center justify-center backdrop-blur-md transition-all active:scale-90 ${
        active
          ? 'bg-red-500/85 text-white shadow-lg shadow-red-500/40'
          : 'bg-black/40 text-white/85 hover:bg-black/60 border border-white/15'
      } ${className}`}
    >
      <Heart size={17} fill={active ? 'currentColor' : 'none'} strokeWidth={2.2} />
    </button>
  );
}
